import {Network} from "../../../network/Network";
import {ProductType} from "../../../models/product/ProductType";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {Services} from "../../Services";
import {ErrorCode} from "../../../network/status/error/ErrorCode";
import {Resources} from "../../../resources/Resources";
import {
    Component
} from "../../../sedestral/framework/interface/component/Component";
import {ICommunityArticleAutopilotIncomingModel} from "../../../models/community/autopilot/ICommunityArticleAutopilotIncomingModel";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {
    ICommunityArticleAutopilotOutgoingModel
} from "../../../models/community/autopilot/ICommunityArticleAutopilotOutgoingModel";
import {WebSocketCommunityEventName} from "../../../network/socket/names/WebSocketCommunityEventName";
import {arrayRemove} from "../../../sedestral/framework/utilities/ArrayRemove";
import {arrayShuffle} from "../../../sedestral/framework/utilities/ArrayShuffle";
import {DayType} from "../../../models/enums/DayType";
import {CommunityArticleAutopilotMode} from "../../../models/community/autopilot/CommunityArticleAutopilotMode";
import {CommunityArticleImageType} from "../../../models/community/article/image/CommunityArticleImageType";
import {CommunityArticleCoverType} from "../../../models/community/article/image/CommunityArticleCoverType";
import {
    CommunityArticleProposalsItemCrawlingType
} from "../../../models/community/article/ai/proposals/CommunityArticleProposalsItemCrawlingType";

export class CommunityArticleAutopilotService {

    public static get DEFAULT_CONFIG(): ICommunityArticleAutopilotIncomingModel {
        return {
            mode: CommunityArticleAutopilotMode.AUTO,
            days: this.randomWeekDays(),
            dayRanks: {},
            importPartner: true,
            publishPartner: true,
            sendNotification: true,
            includeSummary: false,
            includeFaq: true,
            userContext: "",
            internalMesh: true,
            imageType: CommunityArticleImageType.WEBSITE,
            brandImages: true,
            coverTypes: [CommunityArticleCoverType.PHOTOGRAPH_BRANDED, CommunityArticleCoverType.ILLUSTRATION_BRANDED],
            ctaInsertion: true,
            ctaSelectedIds: [],
            documentIds: [],
            folderIds: [],
            knowledgeAutoSearch: true,
            documentsNone: false,
            serpCrawlingType: CommunityArticleProposalsItemCrawlingType.ORGANIC,
            writersRedactorsIds: [],
            writersAccountsIds: [],
            articlesPerDay: 1,
            publishHourMin: -1,
            publishHourMax: -1,
        };
    }

    public static autopilotEvents: ((autopilot: ICommunityArticleAutopilotOutgoingModel) => void)[] = [];

    public static dispose(): void {
        this.autopilotEvents = [];
    }

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AUTOPILOT_UPDATE, (data: ICommunityArticleAutopilotOutgoingModel) => {
            this.autopilotEvents.forEach(callback => callback(data));
        });
    }

    public static onAutopilotUpdate(component: INetworkComponent, callback: (autopilot: ICommunityArticleAutopilotOutgoingModel) => void): void {
        this.autopilotEvents.push(callback);
        component.onRemove(() => arrayRemove(this.autopilotEvents, callback));
    }

    public static async get(communityId: string, component?: INetworkComponent): Promise<ICommunityArticleAutopilotOutgoingModel> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/autopilot/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async update(communityId: string, communityAutopilot: ICommunityArticleAutopilotIncomingModel, component: Component): Promise<ICommunityArticleAutopilotOutgoingModel> {
        Services.handleErrors(component, [
            {errorCode: ErrorCode.COMMUNITY_AUTOPILOT_DAYS_MAX, message: Resources.t("words.communityAutopilotDaysMax")}
        ]);
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/autopilot/update/${communityId}`, communityAutopilot);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async addCluster(communityId: string, clusterId: string, component?: INetworkComponent): Promise<ICommunityArticleAutopilotOutgoingModel> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/autopilot/cluster/add/${communityId}/${clusterId}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async deleteCluster(communityId: string, clusterId: string, component?: INetworkComponent): Promise<ICommunityArticleAutopilotOutgoingModel> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/autopilot/cluster/delete/${communityId}/${clusterId}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    private static randomWeekDays(): DayType[] {
        const weekDays = [DayType.MONDAY, DayType.TUESDAY, DayType.WEDNESDAY, DayType.THURSDAY, DayType.FRIDAY];
        arrayShuffle(weekDays);

        const firstDay = weekDays[0];
        const spacedDays = weekDays.filter(day => Math.abs(day - firstDay) > 1);
        return [firstDay, spacedDays[0]].sort((first, second) => first - second);
    }
}